import { IParkingSubscription } from '../graphql/users/queryUser'
import { ReservationStatus } from '../graphql/tenant/getTenant'

export interface DayEntry {
  day: string
  places: string[]
  cancelled: boolean
}

export const MAX_LISTED_DAYS = 90

export const daysBetween = (fromIso?: string, toIso?: string) => {
  if (!fromIso || !toIso) return []
  const cursor = new Date(`${fromIso.slice(0, 10)}T12:00:00.000Z`)
  const last = new Date(`${toIso.slice(0, 10)}T12:00:00.000Z`)
  const days: string[] = []
  while (cursor <= last && days.length < MAX_LISTED_DAYS) {
    days.push(cursor.toISOString())
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return days
}

export const parkingsOfSubscription = (sub: IParkingSubscription) =>
  (sub.reservations ?? []).reduce<string[]>(
    (names, r) => {
      const name = r.place?.parking?.name
      if (name && !names.includes(name)) names.push(name)
      return names
    },
    sub.parkingPass?.parking?.name ? [sub.parkingPass.parking.name] : [],
  )

export const daysOfSubscription = (
  sub: IParkingSubscription,
  dropped: string[] = [],
): DayEntry[] => {
  const isDropped = (iso: string) => dropped.includes(iso.slice(0, 10))

  const placesByDay = new Map<string, string[]>()
  const byDay = new Map<string, DayEntry>()
  sub.reservations?.forEach(r => {
    if (!r.validFrom) return
    const key = r.validFrom.slice(0, 10)
    const entry = byDay.get(key) ?? {
      day: r.validFrom,
      places: [],
      cancelled: true,
    }
    const place = [r.place?.parking?.name, r.place?.name]
      .filter(Boolean)
      .join(' · ')
    if (place && !entry.places.includes(place)) entry.places.push(place)
    if (r.status !== ReservationStatus.cancelled) entry.cancelled = false
    byDay.set(key, entry)
    placesByDay.set(key, entry.places)
  })

  if (sub.daySlots?.length)
    return sub.daySlots
      .filter(
        slot =>
          !!slot.day &&
          slot.status !== ReservationStatus.cancelled &&
          !isDropped(slot.day),
      )
      .map(slot => ({
        day: slot.day,
        places: placesByDay.get(slot.day.slice(0, 10)) ?? [],
        cancelled: false,
      }))
      .sort((a, b) => a.day.localeCompare(b.day))

  if (byDay.size)
    return Array.from(byDay.values())
      .filter(entry => !entry.cancelled && !isDropped(entry.day))
      .sort((a, b) => a.day.localeCompare(b.day))

  const passParking = sub.parkingPass?.parking?.name
  return daysBetween(sub.validFrom, sub.validTo)
    .filter(day => !isDropped(day))
    .map(day => ({
      day,
      places: passParking ? [passParking] : ([] as string[]),
      cancelled: false,
    }))
}
